import React, { useEffect, useMemo } from "react";
import { Flex, Progress, Text } from "@chakra-ui/react";
import { CheckCircleIcon } from "@chakra-ui/icons";
import { ArrowIcon } from "./arrow-icon";
import { useTranslation } from "react-i18next";
import {
  CourseDescContainer,
  CourseDescText,
  CourseMetaInfo,
  CourseProgressWrapper,
  CourseProgressText,
} from "../styles";
import { formatDateToISO } from "@root/utils/date-helper";
import useMediaQ from "@root/hooks/useMediaQ";
import { useCourseManagement } from "@root/hooks/useCourseManagement";
import { CalenderIcon } from "@root/assets/icons";

const EnrolledCourseDesc: React.FC<any> = ({ course }) => {
  const courseData = course?.course;
  const { t } = useTranslation("common");
  const { sm } = useMediaQ();
  const isMobile = !sm;

  // Fetch chapters and progress for this course to compute video stats
  const {
    courseChaptersData,
    getCourseChapters,
    courseProgressData,
    getCourseProgressById,
  } = useCourseManagement();

  useEffect(() => {
    if (!courseData?.id) return;
    getCourseChapters(Number(courseData.id));
    getCourseProgressById(Number(courseData.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseData?.id]);

  const { totalVideos, completedVideos, progressValue } = useMemo(() => {
    const chapters = (courseChaptersData as any)?.rows || [];

    const total = chapters.reduce((sum: number, chapter: any) => {
      return sum + (chapter?.courseVideos?.length || 0);
    }, 0);

    const progressArray = Array.isArray(courseProgressData?.[0])
      ? courseProgressData[0]
      : Array.isArray(courseProgressData)
      ? courseProgressData
      : [];

    // Create a map of video progress by video ID
    const videoProgressMap = new Map<number, number>();
    progressArray.forEach((item: any) => {
      const videoId = item?.video?.id;
      if (videoId !== null && videoId !== undefined) {
        // Treat a completed video as 100%. The in-course player marks completion
        // via the `completed` boolean and can leave the numeric `progress` below
        // 100. Reading only `progress` here made completed videos show 0% on the
        // dashboard while the course view (which reads `completed`) showed 100%.
        // Use `completed` as the source of truth, falling back to numeric progress.
        const rawProgress =
          typeof item?.progress === "number"
            ? item.progress
            : parseFloat(item?.progress || "0");
        const progress = item?.completed === true ? 100 : rawProgress;
        if (!Number.isNaN(progress)) {
          videoProgressMap.set(videoId, Math.max(0, Math.min(100, progress)));
        }
      }
    });

    // Count completed videos (progress === 100)
    const completedVideoIds = new Set<number>();
    videoProgressMap.forEach((progress, videoId) => {
      if (progress === 100) {
        completedVideoIds.add(videoId);
      }
    });

    let completed = 0;
    chapters.forEach((chapter: any) => {
      (chapter?.courseVideos || []).forEach((video: any) => {
        if (video?.id && completedVideoIds.has(video.id)) {
          completed += 1;
        }
      });
    });

    // Calculate total progress based on individual video progress percentages
    // Each video contributes equally: 100% / total videos
    // Sum all video progress percentages
    let totalProgressSum = 0;
    chapters.forEach((chapter: any) => {
      (chapter?.courseVideos || []).forEach((video: any) => {
        if (video?.id) {
          const videoProgress = videoProgressMap.get(video.id) || 0;
          // Each video contributes: (video progress / 100) * (100 / total videos)
          // Simplified: video progress / total videos
          totalProgressSum += videoProgress;
        }
      });
    });

    // Calculate average progress: sum of all video progress / total videos
    const calculatedProgress = total > 0 ? totalProgressSum / total : 0;
    const roundedProgress = Math.round(calculatedProgress);

    return {
      totalVideos: total,
      completedVideos: completed,
      progressValue: roundedProgress,
    };
  }, [courseChaptersData, courseProgressData]);

  return (
    <CourseDescContainer>
      <Flex direction="column" gap="4px">
        <CourseDescText>{courseData?.title}</CourseDescText>

        <CourseMetaInfo>
          <CheckCircleIcon color="#00A177" boxSize={3.5} />
          <Flex>
            <Text>{completedVideos}</Text>
            {"/"} <Text>{totalVideos}</Text>
          </Flex>
          {isMobile ? (
            <Flex w="100%" alignItems="center" gap="8px">
              <ArrowIcon boxSize={4} />
              <Text whiteSpace="nowrap">
                {t("certificateSection.lastAccessed")}:{" "}
                {formatDateToISO(courseData?.updatedAt)}
              </Text>
            </Flex>
          ) : (
            <>
              <ArrowIcon boxSize={4} />
              <Text whiteSpace="nowrap">
                {t("certificateSection.lastAccessed")}:{" "}
                {formatDateToISO(courseData?.updatedAt)}
              </Text>
            </>
          )}
        </CourseMetaInfo>
        {courseData?.enrollmentPeriodStart && courseData?.enrollmentPeriodEnd && (
          <Flex alignItems="center" gap="8px" mt="6px">
            <CalenderIcon />
            <Text fontSize="14px" fontWeight={400} color="#5C6670">
              {t("courseDetail.enrollmentPeriod")}:{" "}
              <Text as="span" fontWeight={500} fontSize="14px" color="#5C6670">
                {formatDateToISO(
                  new Date(courseData?.enrollmentPeriodStart),
                )?.replace(/-/g, ".")}{" "}
                ~{" "}
                {formatDateToISO(
                  new Date(courseData?.enrollmentPeriodEnd),
                )?.replace(/-/g, ".")}
              </Text>
            </Text>
          </Flex>
        )}
      </Flex>

      {!isMobile && (
        <CourseProgressWrapper display={'flex'}>
          <CourseProgressText>{progressValue}%</CourseProgressText>
          <Progress
            value={progressValue}
            height="12px"
            width="100%"
            colorScheme="blue"
            borderRadius="40px"
          />
        </CourseProgressWrapper>
      )}
    </CourseDescContainer>
  );
};

export default EnrolledCourseDesc;
